import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, Trash2, Wand2, Pencil, Search, X, Sparkles, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  createArticle, generateBrief, fetchArticles, fetchBriefQueue,
  type BriefQueueRow,
} from "@/lib/contentOps/api";
import type { ArticleRow } from "@/lib/contentOps/types";
import { toSinaiStatus, SINAI_META, type SinaiStatus } from "./sinaiStatus";

interface TopicIdea {
  keyword: string;
  intent: string;
  business_value: number;
  opportunity: number;
  rationale: string;
}

// 0-100 quality score: rewards revenue value + easy competition, slight bump for commercial intent.
const scoreIdea = (idea: TopicIdea): number => {
  const value = Math.max(1, Math.min(5, idea.business_value));
  const ease = 6 - Math.max(1, Math.min(5, idea.opportunity)); // 5 (easy) best, 1 (hard) worst
  const intentBoost =
    idea.intent === "commercial" ? 1.0 :
    idea.intent === "transactional" ? 0.9 :
    idea.intent === "informational" ? 0.7 : 0.5;
  // weighted: value 55%, ease 35%, intent 10%
  const raw = (value / 5) * 0.55 + (ease / 5) * 0.35 + intentBoost * 0.10;
  return Math.round(raw * 100);
};

const scoreTier = (s: number) => {
  if (s >= 78) return { label: "Top Pick", color: "hsl(142 75% 72%)", bg: "hsl(142 70% 35% / 0.22)", border: "hsl(142 60% 45%)" };
  if (s >= 60) return { label: "Strong", color: "hsl(48 90% 72%)", bg: "hsl(48 90% 45% / 0.18)", border: "hsl(48 60% 40%)" };
  return { label: "Fair", color: "hsl(0 0% 78%)", bg: "hsl(0 0% 18%)", border: "hsl(0 0% 25%)" };
};

interface Topic {
  id: string;
  keyword: string;
  language: string;
  intent: string | null;
  business_value: number;
  opportunity: number;
  source: string;
  status: string;
  notes: string | null;
  metrics: Record<string, unknown>;
}

const INTENTS = ["informational", "commercial", "transactional", "navigational"];
const PILLARS = [
  "Lead Generation", "Appointment Setting", "Outsourcing", "Virtual Assistants",
  "CRM", "Cold Calling", "Inside Sales", "Business Growth",
  "Customer Support", "Real Estate Operations", "Revenue Operations",
];
type GroupKey = "pillar" | "intent" | "priority";
const GROUP_LABELS: Record<GroupKey, string> = {
  pillar: "Pillar", intent: "Intent", priority: "Priority",
};

// Coverage filter: has an article already been written for this topic?
type CoverageKey = "all" | "uncovered" | "in_progress" | "published";
const COVERAGE_FILTERS: { key: CoverageKey; label: string; color: string; bg: string }[] = [
  { key: "all", label: "All topics", color: "hsl(0 0% 85%)", bg: "hsl(0 0% 16%)" },
  { key: "uncovered", label: "No article yet", color: "hsl(207 70% 78%)", bg: "hsl(207 68% 45% / 0.16)" },
  { key: "in_progress", label: "In progress", color: "hsl(38 92% 72%)", bg: "hsl(38 92% 50% / 0.16)" },
  { key: "published", label: "Article published", color: "hsl(142 65% 70%)", bg: "hsl(142 60% 40% / 0.18)" },
];

interface Props { onOpenArticle: (id: string) => void }

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "").slice(0, 80);

// Competition chip (derived from opportunity score 1=easy, 5=hard)
const competitionLabel = (opp: number) => {
  if (opp <= 2) return { label: "Quick Win", color: "hsl(142 80% 80%)", bg: "hsl(142 70% 35% / 0.30)" };
  if (opp === 3) return { label: "Medium Competition", color: "hsl(38 98% 82%)", bg: "hsl(38 92% 50% / 0.30)" };
  return { label: "High Competition", color: "hsl(0 90% 84%)", bg: "hsl(0 80% 50% / 0.30)" };
};

const intentLabel = (intent: string | null) => {
  if (!intent) return null;
  return { informational: "Informational", commercial: "Commercial", transactional: "Transactional", navigational: "Navigational" }[intent] ?? intent;
};

// Sinai-style "section code" derived deterministically from topic id (e.g. 7A, 7B, 7C)
const sectionCode = (id: string) => {
  const letters = "ABCDEFGH";
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) & 0xffff;
  return `${(h % 9) + 1}${letters[h % letters.length]}`;
};

const TopicsLibraryView = ({ onOpenArticle }: Props) => {
  const [topics, setTopics] = useState<Topic[]>([]);
  const [articles, setArticles] = useState<ArticleRow[]>([]);
  const [briefs, setBriefs] = useState<BriefQueueRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [groupBy, setGroupBy] = useState<GroupKey>("pillar");
  const [coverage, setCoverage] = useState<CoverageKey>("all");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [showNew, setShowNew] = useState(false);
  const [newKw, setNewKw] = useState("");
  const [newPillar, setNewPillar] = useState(PILLARS[0]);
  const [newIntent, setNewIntent] = useState<string>("informational");
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editKw, setEditKw] = useState("");

  // AI suggest panel
  const [showAi, setShowAi] = useState(false);
  const [aiPillar, setAiPillar] = useState(PILLARS[0]);
  const [aiIntent, setAiIntent] = useState<string>("mixed");
  const [aiSeed, setAiSeed] = useState("");
  const [aiCount, setAiCount] = useState(8);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiIdeas, setAiIdeas] = useState<TopicIdea[]>([]);
  const [aiPicked, setAiPicked] = useState<Set<number>>(new Set());
  const [aiSaving, setAiSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, a, b] = await Promise.all([
        supabase.from("content_topics").select("*").order("created_at", { ascending: false }),
        fetchArticles(),
        fetchBriefQueue(),
      ]);
      if (t.error) throw t.error;
      setTopics((t.data ?? []) as Topic[]);
      setArticles(a);
      setBriefs(b);
    } catch (e) {
      toast.error("Failed to load topics", { description: (e as Error).message });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const briefByArticleId = useMemo(() => {
    const m = new Map<string, BriefQueueRow["brief"]>();
    for (const r of briefs) m.set(r.article.id, r.brief);
    return m;
  }, [briefs]);

  const articleForTopic = (kw: string) =>
    articles.find((a) => a.title.toLowerCase().includes(kw.toLowerCase()));

  // Which editorial stage each topic has already reached, used for filtering and counts.
  const coverageById = useMemo(() => {
    const m = new Map<string, SinaiStatus>();
    for (const t of topics) {
      const article = articleForTopic(t.keyword);
      const brief = article ? briefByArticleId.get(article.id) : undefined;
      m.set(t.id, article ? toSinaiStatus(article.status, brief?.status) : "Not Started");
    }
    return m;
  }, [topics, articles, briefByArticleId]);

  const coverageCount = (key: CoverageKey) => {
    let n = 0;
    for (const t of topics) {
      if (t.status === "rejected") continue;
      const s = coverageById.get(t.id) ?? "Not Started";
      if (key === "all") n++;
      else if (key === "published" && s === "Published") n++;
      else if (key === "uncovered" && s === "Not Started") n++;
      else if (key === "in_progress" && s !== "Published" && s !== "Not Started") n++;
    }
    return n;
  };

  const add = async () => {
    if (!newKw.trim()) return;
    setAdding(true);
    const { error } = await supabase.from("content_topics").insert({
      keyword: newKw.trim(),
      source: "manual",
      intent: newIntent,
      notes: newPillar,
    } as never);
    setAdding(false);
    if (error) toast.error("Couldn't add", { description: error.message });
    else { setNewKw(""); setShowNew(false); toast.success("Topic added"); load(); }
  };

  const updateTopic = async (id: string, patch: Partial<Topic>) => {
    setTopics((rs) => rs.map((r) => r.id === id ? { ...r, ...patch } : r));
    await supabase.from("content_topics").update(patch as never).eq("id", id);
  };

  const archive = async (id: string) => {
    if (!confirm("Archive this topic?")) return;
    await supabase.from("content_topics").delete().eq("id", id);
    setTopics((rs) => rs.filter((r) => r.id !== id));
    toast.success("Topic archived");
  };

  const saveEdit = async () => {
    if (!editingId || !editKw.trim()) return;
    await updateTopic(editingId, { keyword: editKw.trim() });
    setEditingId(null);
  };

  const generateBriefFromTopic = async (t: Topic) => {
    setBusyId(t.id);
    try {
      const pillar = t.notes && PILLARS.includes(t.notes) ? t.notes : PILLARS[0];
      let article = articleForTopic(t.keyword);
      if (!article) {
        const title = t.keyword.replace(/\b\w/g, (c) => c.toUpperCase());
        article = await createArticle({ title, slug: slugify(t.keyword), category: pillar });
      }
      await generateBrief({
        article_id: article.id,
        topic: t.keyword,
        target_keywords: [t.keyword],
        persona: "B2B operators, founders, and revenue leaders",
        cluster_name: pillar,
        notes: t.notes ?? undefined,
      });
      await updateTopic(t.id, { status: "in_progress" });
      toast.success("Brief generated", { description: "Open the Brief Generator tab to review." });
      load();
    } catch (e) {
      toast.error("Couldn't generate brief", { description: (e as Error).message });
    } finally {
      setBusyId(null);
    }
  };

  const runAiSuggest = async () => {
    setAiLoading(true);
    setAiIdeas([]);
    setAiPicked(new Set());
    try {
      const { data, error } = await supabase.functions.invoke("generate-topic-ideas", {
        body: {
          pillar: aiPillar,
          intent: aiIntent === "mixed" ? undefined : aiIntent,
          count: aiCount,
          seed: aiSeed.trim() || undefined,
          existing_keywords: topics.map((t) => t.keyword),
        },
      });
      if (error) throw error;
      const raw = (data?.ideas ?? []) as TopicIdea[];
      if (!raw.length) throw new Error("No ideas returned");
      // Sort by quality score, highest first, so top picks surface to the top.
      const ideas = [...raw].sort((a, b) => scoreIdea(b) - scoreIdea(a));
      setAiIdeas(ideas);
      // Pre-select only Strong+ tier (score >= 60) so top picks save by default.
      setAiPicked(new Set(ideas.map((idea, i) => [idea, i] as const).filter(([idea]) => scoreIdea(idea) >= 60).map(([, i]) => i)));
    } catch (e) {
      toast.error("AI couldn't generate ideas", { description: (e as Error).message });
    } finally {
      setAiLoading(false);
    }
  };

  const saveAiPicked = async () => {
    const picked = aiIdeas.filter((_, i) => aiPicked.has(i));
    if (!picked.length) return;
    setAiSaving(true);
    try {
      const rows = picked.map((p) => ({
        keyword: p.keyword.trim().toLowerCase(),
        source: "ai",
        intent: p.intent,
        business_value: Math.max(1, Math.min(5, p.business_value)),
        opportunity: Math.max(1, Math.min(5, p.opportunity)),
        notes: aiPillar,
      }));
      const { error } = await supabase.from("content_topics").insert(rows as never);
      if (error) throw error;
      toast.success(`Added ${rows.length} AI topic${rows.length > 1 ? "s" : ""}`);
      setShowAi(false);
      setAiIdeas([]);
      setAiPicked(new Set());
      load();
    } catch (e) {
      toast.error("Couldn't save topics", { description: (e as Error).message });
    } finally {
      setAiSaving(false);
    }
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return topics.filter((t) => {
      if (t.status === "rejected") return false;
      const s = coverageById.get(t.id) ?? "Not Started";
      if (coverage === "published" && s !== "Published") return false;
      if (coverage === "uncovered" && s !== "Not Started") return false;
      if (coverage === "in_progress" && (s === "Published" || s === "Not Started")) return false;
      if (!q) return true;
      return t.keyword.toLowerCase().includes(q) || (t.notes ?? "").toLowerCase().includes(q);
    });
  }, [topics, search, coverage, coverageById]);

  const groups = useMemo(() => {
    const out = new Map<string, Topic[]>();
    for (const t of visible) {
      let key = "Uncategorized";
      if (groupBy === "pillar") key = (t.notes && PILLARS.includes(t.notes)) ? t.notes : "Uncategorized";
      else if (groupBy === "intent") key = intentLabel(t.intent) ?? "Unspecified";
      else key = `Priority ${t.business_value}`;
      if (!out.has(key)) out.set(key, []);
      out.get(key)!.push(t);
    }
    return Array.from(out.entries()).sort(([a], [b]) => a.localeCompare(b));
  }, [visible, groupBy]);

  return (
    <div className="space-y-5">
      {/* Toolbar: search + group + new */}
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="relative flex-1 min-w-[260px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "hsl(0 0% 45%)" }} />
          <Input value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search topics, keywords, pillars…"
            className="border pl-9 h-10"
            style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
        </div>
        <Select value={groupBy} onValueChange={(v) => setGroupBy(v as GroupKey)}>
          <SelectTrigger className="h-10 w-52 border"
            style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 85%)" }}>
            <span className="text-xs" style={{ color: "hsl(0 0% 55%)" }}>Group by </span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {(Object.keys(GROUP_LABELS) as GroupKey[]).map((g) => (
              <SelectItem key={g} value={g}>{GROUP_LABELS[g]}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Button onClick={() => setShowAi((v) => !v)}
          className="h-10 font-semibold"
          style={{ background: "hsl(265 60% 32%)", color: "hsl(265 80% 92%)" }}>
          <Sparkles size={14} className="mr-1" /> AI Suggest
        </Button>
        <Button onClick={() => setShowNew(true)}
          className="h-10 font-semibold"
          style={{ background: "hsl(142 50% 30%)", color: "hsl(42 60% 90%)" }}>
          <Plus size={14} className="mr-1" /> New Topic
        </Button>
      </div>

      {/* Coverage filter: already written, still open, or untouched */}
      <div className="flex flex-wrap items-center gap-2">
        {COVERAGE_FILTERS.map((f) => {
          const on = coverage === f.key;
          return (
            <button key={f.key} onClick={() => setCoverage(f.key)}
              className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors"
              style={{
                background: on ? f.bg : "hsl(0 0% 6%)",
                borderColor: on ? f.color : "hsl(0 0% 18%)",
                color: on ? f.color : "hsl(0 0% 65%)",
              }}>
              {f.label}
              <span className="text-[10px] font-bold" style={{ color: on ? f.color : "hsl(0 0% 48%)" }}>
                {coverageCount(f.key)}
              </span>
            </button>
          );
        })}
        {coverage !== "all" && (
          <button onClick={() => setCoverage("all")}
            className="text-xs underline" style={{ color: "hsl(0 0% 60%)" }}>
            Show all topics
          </button>
        )}
      </div>

      {/* AI suggest panel */}
      {showAi && (
        <div className="rounded-xl border p-4 space-y-4"
          style={{ background: "hsl(265 30% 8%)", borderColor: "hsl(265 40% 22%)" }}>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(265 30% 70%)" }}>Seed / angle (optional)</label>
              <Input value={aiSeed} onChange={(e) => setAiSeed(e.target.value)}
                placeholder="e.g. SaaS founders evaluating outsourced SDRs"
                className="border" style={{ background: "hsl(265 30% 6%)", borderColor: "hsl(265 30% 22%)", color: "hsl(0 0% 92%)" }} />
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(265 30% 70%)" }}>Pillar</label>
              <Select value={aiPillar} onValueChange={setAiPillar}>
                <SelectTrigger className="w-56 text-xs" style={{ background: "hsl(265 30% 6%)", borderColor: "hsl(265 30% 22%)" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>{PILLARS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(265 30% 70%)" }}>Intent</label>
              <Select value={aiIntent} onValueChange={setAiIntent}>
                <SelectTrigger className="w-40 text-xs capitalize" style={{ background: "hsl(265 30% 6%)", borderColor: "hsl(265 30% 22%)" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mixed">Mixed</SelectItem>
                  {INTENTS.map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(265 30% 70%)" }}>Count</label>
              <Input type="number" min={3} max={15} value={aiCount}
                onChange={(e) => setAiCount(Math.max(3, Math.min(15, Number(e.target.value) || 8)))}
                className="w-20 border" style={{ background: "hsl(265 30% 6%)", borderColor: "hsl(265 30% 22%)", color: "hsl(0 0% 92%)" }} />
            </div>
            <Button onClick={runAiSuggest} disabled={aiLoading}
              style={{ background: "hsl(265 60% 45%)", color: "hsl(265 80% 95%)" }}>
              {aiLoading ? <Loader2 size={14} className="animate-spin mr-1" /> : <Sparkles size={14} className="mr-1" />}
              {aiIdeas.length ? "Regenerate" : "Generate"}
            </Button>
            <Button variant="ghost" onClick={() => { setShowAi(false); setAiIdeas([]); }}
              style={{ color: "hsl(265 30% 70%)" }}><X size={14} /></Button>
          </div>

          {aiIdeas.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-xs" style={{ color: "hsl(265 30% 70%)" }}>
                  {aiPicked.size} of {aiIdeas.length} selected · pillar: <strong>{aiPillar}</strong>
                </p>
                <div className="flex gap-2">
                  <Button size="sm" variant="ghost" onClick={() => setAiPicked(new Set(aiIdeas.map((_, i) => i)))}
                    style={{ color: "hsl(265 30% 80%)" }}>Select all</Button>
                  <Button size="sm" variant="ghost" onClick={() => setAiPicked(new Set())}
                    style={{ color: "hsl(265 30% 80%)" }}>Clear</Button>
                </div>
              </div>
              <div className="space-y-1.5 max-h-[420px] overflow-y-auto pr-1">
                {aiIdeas.map((idea, i) => {
                  const on = aiPicked.has(i);
                  const score = scoreIdea(idea);
                  const tier = scoreTier(score);
                  const isTop = score >= 78;
                  return (
                    <button key={i} onClick={() => {
                      const next = new Set(aiPicked);
                      on ? next.delete(i) : next.add(i);
                      setAiPicked(next);
                    }}
                      className="w-full text-left rounded-lg border p-3 flex items-start gap-3 transition-colors relative"
                      style={{
                        background: on
                          ? (isTop ? "hsl(142 35% 12%)" : "hsl(265 40% 14%)")
                          : "hsl(265 30% 7%)",
                        borderColor: on
                          ? (isTop ? tier.border : "hsl(265 60% 45%)")
                          : (isTop ? "hsl(142 40% 30%)" : "hsl(265 30% 18%)"),
                        boxShadow: isTop ? `0 0 0 1px ${tier.border} inset, 0 4px 18px -8px hsl(142 60% 35% / 0.5)` : undefined,
                      }}>
                      <div className="mt-0.5 w-4 h-4 rounded border flex items-center justify-center shrink-0"
                        style={{ borderColor: on ? "hsl(265 60% 60%)" : "hsl(265 30% 35%)", background: on ? "hsl(265 60% 45%)" : "transparent" }}>
                        {on && <Check size={11} style={{ color: "hsl(265 80% 95%)" }} />}
                      </div>

                      {/* Score gauge */}
                      <div className="flex flex-col items-center justify-center shrink-0 rounded-md px-2 py-1.5 border"
                        style={{ background: tier.bg, borderColor: tier.border, minWidth: 52 }}>
                        <div className="text-base font-bold leading-none" style={{ color: tier.color }}>{score}</div>
                        <div className="text-[9px] uppercase tracking-wide mt-0.5" style={{ color: tier.color, opacity: 0.85 }}>{tier.label}</div>
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium truncate" style={{ color: "hsl(0 0% 95%)" }}>{idea.keyword}</div>
                          {isTop && (
                            <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                              style={{ background: "hsl(142 70% 45%)", color: "hsl(0 0% 8%)" }}>★ Top</span>
                          )}
                        </div>
                        <div className="text-xs mt-0.5" style={{ color: "hsl(265 20% 70%)" }}>{idea.rationale}</div>
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          <Chip color="hsl(207 68% 80%)" bg="hsl(207 68% 40% / 0.18)">{idea.intent}</Chip>
                          <Chip color="hsl(142 70% 70%)" bg="hsl(142 70% 35% / 0.16)">Value {idea.business_value}/5</Chip>
                          <Chip color="hsl(38 92% 75%)" bg="hsl(38 92% 45% / 0.16)">Opp {idea.opportunity}/5</Chip>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
              <div className="flex justify-end pt-1">
                <Button onClick={saveAiPicked} disabled={aiSaving || aiPicked.size === 0}
                  style={{ background: "hsl(142 50% 30%)", color: "hsl(42 60% 90%)" }}>
                  {aiSaving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />}
                  Add {aiPicked.size} topic{aiPicked.size === 1 ? "" : "s"}
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* New topic inline form */}
      {showNew && (
        <div className="rounded-xl border p-4 flex flex-wrap items-end gap-3"
          style={{ background: "hsl(0 0% 7%)", borderColor: "hsl(0 0% 14%)" }}>
          <div className="flex-1 min-w-[240px]">
            <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>Topic / keyword</label>
            <Input value={newKw} autoFocus onChange={(e) => setNewKw(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && add()}
              placeholder="e.g. outsourced appointment setting for B2B SaaS"
              className="border" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)", color: "hsl(0 0% 90%)" }} />
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>Pillar</label>
            <Select value={newPillar} onValueChange={setNewPillar}>
              <SelectTrigger className="w-56 text-xs" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)" }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{PILLARS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium mb-1.5 block" style={{ color: "hsl(0 0% 55%)" }}>Intent</label>
            <Select value={newIntent} onValueChange={setNewIntent}>
              <SelectTrigger className="w-40 text-xs capitalize" style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 18%)" }}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>{INTENTS.map((x) => <SelectItem key={x} value={x} className="capitalize">{x}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <Button onClick={add} disabled={adding || !newKw.trim()}
            style={{ background: "hsl(142 50% 30%)", color: "hsl(42 60% 90%)" }}>
            {adding ? <Loader2 size={14} className="animate-spin mr-1" /> : <Plus size={14} className="mr-1" />} Save
          </Button>
          <Button variant="ghost" onClick={() => setShowNew(false)}
            style={{ color: "hsl(0 0% 60%)" }}><X size={14} /></Button>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40" style={{ color: "hsl(0 0% 50%)" }}>
          <Loader2 size={16} className="animate-spin mr-2" /> Loading topics…
        </div>
      ) : groups.length === 0 ? (
        <div className="rounded-xl border p-12 text-center"
          style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
          <p className="text-base font-medium" style={{ color: "hsl(0 0% 80%)" }}>
            {coverage !== "all" || search.trim() ? "No topics match this filter" : "No topics yet"}
          </p>
          <p className="text-sm mt-1" style={{ color: "hsl(0 0% 55%)" }}>
            {coverage !== "all" || search.trim()
              ? "Try another coverage filter, or clear the search box."
              : 'Click "New Topic" above to start the editorial workflow.'}
          </p>
          {(coverage !== "all" || search.trim()) && (
            <Button variant="outline"
              onClick={() => { setCoverage("all"); setSearch(""); }}
              className="mt-4 border"
              style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 90%)", background: "transparent" }}>
              Clear filters
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-7">
          {groups.map(([groupKey, items]) => (
            <section key={groupKey} className="space-y-2">
              <div className="flex items-baseline gap-2">
                <h3 className="text-base font-semibold" style={{ color: "hsl(0 0% 90%)" }}>{groupKey}</h3>
                <span className="text-sm" style={{ color: "hsl(0 0% 50%)" }}>({items.length})</span>
              </div>
              <div className="space-y-2">
                {items.map((t) => {
                  const article = articleForTopic(t.keyword);
                  const brief = article ? briefByArticleId.get(article.id) : undefined;
                  const sinai = article ? toSinaiStatus(article.status, brief?.status) : "Not Started";
                  const sm = SINAI_META[sinai];
                  const comp = competitionLabel(t.opportunity);
                  const intent = intentLabel(t.intent);
                  const pillar = (t.notes && PILLARS.includes(t.notes)) ? t.notes : null;
                  const busy = busyId === t.id;
                  const isEditing = editingId === t.id;

                  return (
                    <div key={t.id} className="rounded-xl border p-4 flex flex-wrap gap-3 items-center"
                      style={{ background: "hsl(0 0% 6%)", borderColor: "hsl(0 0% 14%)" }}>
                      <div className="min-w-0 flex-1 space-y-2">
                        {isEditing ? (
                          <div className="flex items-center gap-2">
                            <Input value={editKw} autoFocus onChange={(e) => setEditKw(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveEdit(); if (e.key === "Escape") setEditingId(null); }}
                              className="border max-w-md"
                              style={{ background: "hsl(0 0% 5%)", borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 95%)" }} />
                            <Button size="sm" onClick={saveEdit}
                              style={{ background: "hsl(142 50% 30%)", color: "hsl(42 60% 90%)" }}>Save</Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}
                              style={{ color: "hsl(0 0% 60%)" }}>Cancel</Button>
                          </div>
                        ) : (
                          <button onClick={() => article && onOpenArticle(article.id)}
                            className="text-[15px] font-semibold text-left hover:underline truncate"
                            style={{ color: "hsl(0 0% 95%)" }}>
                            {t.keyword.replace(/\b\w/g, (c) => c.toUpperCase())}
                          </button>
                        )}
                        <div className="flex flex-wrap items-center gap-1.5">
                          {pillar && <Chip color="hsl(42 92% 85%)" bg="hsl(42 62% 42% / 0.34)">{pillar}</Chip>}
                          <Chip color="hsl(0 0% 96%)" bg="hsl(0 0% 32%)">{sectionCode(t.id)}</Chip>
                          <Chip color={comp.color} bg={comp.bg}>{comp.label}</Chip>
                          {intent && <Chip color="hsl(207 92% 86%)" bg="hsl(207 72% 45% / 0.32)">{intent}</Chip>}
                          <Chip color={sm.color} bg={sm.bg}>{sinai}</Chip>
                        </div>
                      </div>

                      <div className="flex items-center gap-1 shrink-0">
                        <Button size="sm" variant="outline" disabled={busy}
                          onClick={() => generateBriefFromTopic(t)}
                          className="h-8 border" style={{ borderColor: "hsl(0 0% 22%)", color: "hsl(0 0% 90%)", background: "transparent" }}>
                          {busy ? <Loader2 size={12} className="animate-spin mr-1.5" /> : <Wand2 size={12} className="mr-1.5" />}
                          Brief
                        </Button>
                        <Button size="sm" variant="ghost"
                          onClick={() => { setEditingId(t.id); setEditKw(t.keyword); }}
                          className="h-8 w-8 p-0" style={{ color: "hsl(0 0% 60%)" }}><Pencil size={13} /></Button>
                        <Button size="sm" variant="ghost" onClick={() => archive(t.id)}
                          className="h-8 w-8 p-0" style={{ color: "hsl(0 0% 60%)" }}><Trash2 size={13} /></Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
};

const Chip = ({ color, bg, children }: { color: string; bg: string; children: React.ReactNode }) => (
  <span className="inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-medium"
    style={{ background: bg, color }}>{children}</span>
);

export default TopicsLibraryView;
